import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { getUserRoleRequest, resolveAdminRequest } from '~/api/auth';
import { saveUser, saveUserRole } from '~/reducers/auth';
import Spinner from '~/components/Spinner/Spinner';
import { applyVendorTheme } from '~/theme/vendorTheme';
import { VENDOR_NAME_KEY } from '~/theme/vendorConstants';

export { VENDOR_NAME_KEY };

type AuthSeed = {
  authToken: string;
  emailAddress: string;
  firstName?: string;
  lastName?: string;
  buildingDetails: Array<{ buildingId: number | string; buildingName: string }>;
};

const isAuthSeed = (data: unknown): data is AuthSeed => {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    (d.type === undefined || d.type === 'auth-seed') &&
    typeof d.authToken === 'string' && d.authToken.length > 0 &&
    typeof d.emailAddress === 'string' && d.emailAddress.length > 0 &&
    Array.isArray(d.buildingDetails) && d.buildingDetails.length > 0
  );
};

const VendorLoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const didResolveRef = useRef(false);
  const cachedSeedRef = useRef<AuthSeed | null>(null);

  useEffect(() => {
    console.log('[VendorLogin] effect ran — search:', search, 'didResolve:', didResolveRef.current);

    const params = new URLSearchParams(search);
    const vendor = params.get('host');
    if (vendor) {
      sessionStorage.setItem(VENDOR_NAME_KEY, vendor);
      applyVendorTheme();
    }

    const resolveAuth = async (seed: AuthSeed) => {
      console.log('[VendorLogin] resolveAuth called — didResolveRef:', didResolveRef.current, 'email:', seed.emailAddress);
      if (didResolveRef.current) {
        console.log('[VendorLogin] resolveAuth blocked — already resolved');
        return;
      }
      didResolveRef.current = true;
      cachedSeedRef.current = seed;

      const cookies = new Cookies();
      cookies.remove('token', { path: '/' });

      try {
        console.log('[VendorLogin] calling resolveAdminRequest');
        const res = await resolveAdminRequest(seed);
        console.log('[VendorLogin] resolveAdminRequest response status:', res?.status, 'keys:', Object.keys(res?.data || {}));
        const payload = res?.data || {};
        const token = payload.token;
        const user = payload.user;

        if (token) {
          cookies.set('token', token, { path: '/', httpOnly: false });
          sessionStorage.setItem('token', token);
        }

        if (user) {
          const building = user.building;
          const buildingUuid = building?.uuid ?? (typeof building === 'string' ? building : '');
          dispatch(
            saveUser({
              uuid: user.uuid || '',
              email: user.email || seed.emailAddress || '',
              phone: user.phone || '',
              profileImage: user.profileImage || '',
              verified: user.verified ?? false,
              verifiedPhone: user.verifiedPhone ?? false,
              building: buildingUuid
            })
          );
        }

        const userId = user?.uuid || payload.userUuid || payload.user_id || payload.uuid;
        console.log('[VendorLogin] resolved userId:', userId, 'payload.role:', payload.role);
        if (userId) {
          const roleRes = await getUserRoleRequest(userId);
          dispatch(saveUserRole(roleRes.data));
        } else if (payload.role) {
          dispatch(saveUserRole(payload.role));
        }

        console.log('[VendorLogin] auth success — posting auth-result and redirecting to /building');
        window.parent.postMessage({ type: 'auth-result', success: true }, '*');
        history.replace('/building');
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        const message =
          err?.response?.data?.message || err?.message || 'Authentication failed. Please try again.';
        console.error('[VendorLogin] resolveAdminRequest failed:', message, e);
        window.parent.postMessage({ type: 'auth-result', success: false, error: message }, '*');
        setError(message);
        setRetrying(false);
        didResolveRef.current = false;
      }
    };

    // URL-based login examples:
    //   Single building:
    //   /login?host=acme&authToken=TOKEN&emailAddress=admin@acme.com&buildingId=1&buildingName=Tower
    //   Multiple buildings:
    //   /login?host=acme&authToken=TOKEN&emailAddress=admin@acme.com&buildingId=1&buildingName=Tower&buildingId=2&buildingName=Plaza
    const authToken = params.get('authToken');
    const emailAddress = params.get('emailAddress');
    const buildingIds = params.getAll('buildingId');
    const buildingNames = params.getAll('buildingName');
    if (authToken && emailAddress && buildingIds.length > 0) {
      console.log('[VendorLogin] URL-based auth detected');
      const buildingDetails = buildingIds.map((id, i) => ({
        buildingId: Number.isNaN(Number(id)) ? id : Number(id),
        buildingName: buildingNames[i] ?? ''
      }));
      resolveAuth({
        authToken,
        emailAddress,
        firstName: params.get('firstName') ?? undefined,
        lastName: params.get('lastName') ?? undefined,
        buildingDetails
      });
      return;
    }

    // Wait for auth credentials from the parent frame via postMessage.
    console.log('[VendorLogin] sending vendor-login-ready to parent');
    window.parent.postMessage({ type: 'vendor-login-ready' }, '*');
    const handleMessage = async (event: MessageEvent) => {
      console.log('[VendorLogin] postMessage received — origin:', event.origin, 'isAuthSeed:', isAuthSeed(event.data), 'data:', event.data);
      if (!isAuthSeed(event.data)) return;
      resolveAuth(event.data);
    };

    window.addEventListener('message', handleMessage);
    return () => {
      console.log('[VendorLogin] effect cleanup — removing message listener');
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch, history, search]);

  const handleRetry = useCallback(async () => {
    if (!cachedSeedRef.current) return;
    setRetrying(true);
    setError(null);
    didResolveRef.current = false;
    const params = new URLSearchParams(search);
    const authToken = params.get('authToken');
    const emailAddress = params.get('emailAddress');
    const buildingIds = params.getAll('buildingId');
    const buildingNames = params.getAll('buildingName');
    let seed = cachedSeedRef.current;
    if (authToken && emailAddress && buildingIds.length > 0) {
      seed = {
        authToken,
        emailAddress,
        firstName: params.get('firstName') ?? undefined,
        lastName: params.get('lastName') ?? undefined,
        buildingDetails: buildingIds.map((id, i) => ({
          buildingId: Number.isNaN(Number(id)) ? id : Number(id),
          buildingName: buildingNames[i] ?? ''
        }))
      };
    }
    const cookies = new Cookies();
    cookies.remove('token', { path: '/' });
    try {
      const res = await resolveAdminRequest(seed);
      const payload = res?.data || {};
      const token = payload.token;
      const user = payload.user;
      if (token) {
        cookies.set('token', token, { path: '/', httpOnly: false });
        sessionStorage.setItem('token', token);
      }
      if (user) {
        const building = user.building;
        const buildingUuid = building?.uuid ?? (typeof building === 'string' ? building : '');
        dispatch(
          saveUser({
            uuid: user.uuid || '',
            email: user.email || seed.emailAddress || '',
            phone: user.phone || '',
            profileImage: user.profileImage || '',
            verified: user.verified ?? false,
            verifiedPhone: user.verifiedPhone ?? false,
            building: buildingUuid
          })
        );
      }
      const userId = user?.uuid || payload.userUuid || payload.user_id || payload.uuid;
      if (userId) {
        const roleRes = await getUserRoleRequest(userId);
        dispatch(saveUserRole(roleRes.data));
      } else if (payload.role) {
        dispatch(saveUserRole(payload.role));
      }
      window.parent.postMessage({ type: 'auth-result', success: true }, '*');
      history.replace('/building');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const message =
        err?.response?.data?.message || err?.message || 'Authentication failed. Please try again.';
      window.parent.postMessage({ type: 'auth-result', success: false, error: message }, '*');
      setError(message);
      setRetrying(false);
    }
  }, [dispatch, history, search]);

  const isNoPropertiesError = error?.includes('no properties');

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          textAlign: 'center',
          padding: '32px',
          boxSizing: 'border-box'
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            backgroundColor: '#fde8e8',
            marginBottom: '20px'
          }}
        >
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z'
              stroke='#d9534f'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <line x1='12' y1='8' x2='12' y2='12' stroke='#d9534f' strokeWidth='2' strokeLinecap='round' />
            <circle cx='12' cy='16' r='1' fill='#d9534f' />
          </svg>
        </div>
        <p style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a1a', margin: '0 0 12px', maxWidth: '400px' }}>
          {isNoPropertiesError ? 'No properties enabled for Cobu' : 'Authentication failed'}
        </p>
        <p style={{ fontSize: '14px', color: '#888', margin: '0 0 28px', lineHeight: '1.6', maxWidth: '400px' }}>
          {error}
        </p>
        {cachedSeedRef.current && !isNoPropertiesError && (
          <button
            onClick={handleRetry}
            disabled={retrying}
            style={{
              padding: '10px 28px',
              cursor: retrying ? 'not-allowed' : 'pointer',
              border: 'none',
              background: retrying ? '#ccc' : '#d9534f',
              color: '#fff',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              opacity: retrying ? 0.7 : 1
            }}
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh'
      }}
    >
      <Spinner />
    </div>
  );
};

export default VendorLoginPage;
